import { useState, useEffect } from 'react';
import { Search, UserPlus, Users, X, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import defaultAvatar from '../assets/default-avatar.jpg';
import Footer from '../components/Footer'; // Ajustez le chemin selon votre structure
import NavBar from '../components/NavBar';
import { useNavigate } from 'react-router-dom';

const FindFriends = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recommendationsLoading, setRecommendationsLoading] = useState(true);
  const [searchActive, setSearchActive] = useState(false);
  const [pendingRequests, setPendingRequests] = useState(new Set());
  const [sentRequests, setSentRequests] = useState(new Set());
  const navigate = useNavigate();

  // Charger les recommandations au montage
  useEffect(() => {
    fetchRecommendations();
  }, []);

  // Recherche avec debounce
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      setSearchActive(false);
      return;
    }

    setSearchActive(true);
    const timeoutId = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const fetchRecommendations = async () => {
    try {
      setRecommendationsLoading(true);
      const response = await api.get('/friends/recommendations');
      setRecommendations(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des recommandations:', error);
    } finally {
      setRecommendationsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    try {
      setLoading(true);
      const response = await api.get(`/friends/search?q=${searchQuery}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRequest = async (userId) => {
    try {
      setPendingRequests(prev => new Set(prev).add(userId));
      await api.post(`/users/${userId}/follow`);
      setSentRequests(prev => new Set(prev).add(userId));
      
      // Retirer des recommandations aprÃ¨s envoi
      setRecommendations(prev => prev.filter(user => user._id !== userId));
      setSearchResults(prev => prev.filter(user => user._id !== userId));
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande:', error);
      setPendingRequests(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const getMutualFriendsText = (count) => {
    if (count === 0) return null;
    if (count === 1) return '1 ami commun';
    return `${count} amis communs`;
  };

  const UserCard = ({ user, showMutualFriends = false }) => {
    const isPending = pendingRequests.has(user._id);
    const isSent = sentRequests.has(user._id);

    return (
      
      <div className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-4 hover:border-slate-700 transition-all">
        <div className="flex items-center gap-4">
          {/* Avatar */}
          <img
            src={user.photoProfil || defaultAvatar}
            alt={`${user.prenom} ${user.nom}`}
            className="w-14 h-14 rounded-full object-cover border-2 border-slate-800"
          />

          {/* Info utilisateur */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white text-base truncate">
              {user.prenom} {user.nom}
            </h3>
            {user.ville && (
              <p className="text-sm text-gray-400 truncate">{user.ville}</p>
            )}
            {showMutualFriends && user.mutualFriendsCount > 0 && (
              <div className="flex items-center gap-1.5 mt-1">
                <Users size={14} className="text-indigo-400" />
                <p className="text-xs text-indigo-400">
                  {getMutualFriendsText(user.mutualFriendsCount)}
                </p>
              </div>
            )}
          </div>

          {/* Bouton d'action */}
          {isSent ? (
            <button
              disabled
              className="flex-shrink-0 px-4 py-2 bg-green-500/10 border border-green-500/30 text-green-400 rounded-xl flex items-center gap-2 text-sm font-medium"
            >
              <CheckCircle size={16} />
              <span className="hidden sm:inline">Envoyée</span>
            </button>
          ) : (
            <button
              onClick={() => handleSendRequest(user._id)}
              disabled={isPending}
              className="flex-shrink-0 px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl transition-all disabled:bg-slate-700 disabled:text-gray-500 flex items-center gap-2 text-sm font-medium shadow-lg shadow-indigo-500/20"
            >
              {isPending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span className="hidden sm:inline">Envoi...</span>
                </>
              ) : (
                <>
                  <UserPlus size={16} />
                  <span className="hidden sm:inline">Ajouter</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
  <div className="min-h-screen bg-slate-950 flex flex-col">
    {/* Navbar */}
    <NavBar />
    <main className="max-w-10xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
    <button
      onClick={() => navigate('/')}
      className="flex items-center text-indigo-500 hover:text-indigo-400 font-semibold mb-4 sm:mb-6 bg-slate-900 border border-slate-800 hover:border-indigo-500 px-3 py-2 rounded-lg transition"
    >
      <ArrowLeft size={20} className="mr-2" />
      <span>Retour</span>
    </button>
    {/* Header avec barre de recherche */}
    <div className="sticky top-0 z-20 bg-slate-950/95 backdrop-blur-lg border-b border-slate-800">
      <div className="max-w-2xl mx-auto px-4 py-4">
        <h1 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
          <Users className="text-indigo-500" size={28} />
          Trouver des amis
        </h1>

        {/* Barre de recherche */}
        <div className="relative">
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
            <Search className="text-gray-400" size={20} />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher des coureurs..."
            className="w-full pl-12 pr-12 py-3 bg-slate-900 border border-slate-800 text-white placeholder-gray-500 rounded-2xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all text-base antialiased [-webkit-font-smoothing:antialiased]"
          />
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSearchResults([]);
                setSearchActive(false);
              }}
              className="absolute inset-y-0 right-4 flex items-center text-gray-400 hover:text-gray-300"
            >
              <X size={20} />
            </button>
          )}
        </div>
      </div>
    </div>

    {/* Contenu principal */}
    <div className="flex-1 max-w-2xl mx-auto px-4 py-6 w-full">
      {/* RÃ©sultats de recherche */}
      {searchActive && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">
            Résultats de recherche
          </h2>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="text-indigo-500 animate-spin" size={32} />
            </div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-3">
              {searchResults.map((user) => (
                <UserCard key={user._id} user={user} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Search className="mx-auto text-slate-700 mb-4" size={48} />
              <p className="text-gray-400">Aucun résultat trouvé</p>
              <p className="text-gray-500 text-sm mt-1">
                Essayez avec un autre nom
              </p>
            </div>
          )}
        </div>
      )}

      {/* Recommandations (affichÃ©es seulement si pas de recherche active) */}
      {!searchActive && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Users className="text-indigo-500" size={20} />
            <h2 className="text-lg font-semibold text-white">
              Vous connaissez peut-être
            </h2>
          </div>

          {recommendationsLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="text-indigo-500 animate-spin" size={32} />
            </div>
          ) : recommendations.length > 0 ? (
            <div className="space-y-3">
              {recommendations.map((user) => (
                <UserCard 
                  key={user._id} 
                  user={user} 
                  showMutualFriends={true}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="mx-auto text-slate-700 mb-4" size={48} />
              <p className="text-gray-400">Aucune recommandation pour le moment</p>
              <p className="text-gray-500 text-sm mt-1">
                Ajoutez des amis pour voir plus de suggestions
              </p>
            </div>
          )}
        </div>
      )}
    </div>

    {/* Footer */}
    </main>
    <Footer />
  </div>
  
);
};

export default FindFriends;